import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-notice-banner>` says about itself.
 *
 * A banner and a toast are not two styles of the same thing. A toast is an outcome of something the
 * person just did, it is gone in a few seconds, and nothing is lost if it is missed. A banner is a
 * condition the surface is IN — it was true before the person arrived, it stays true while they
 * work, and it usually wants a decision. Everything about this piece follows from that: it takes
 * the full width of its region, it has an `actions` slot for the decision, and it disappears only
 * when somebody presses the close control on it.
 *
 * Two things worth knowing before writing one.
 *
 * `title` is the ordinary HTML `title` attribute as well, so whatever is set as the banner heading
 * is ALSO the element's hover tooltip. Nothing is broken by it, but it is not a private name.
 *
 * `dismissable` removes the element from the page. The close control raises a cancelable
 * `caos-notice-dismiss`, and if nothing calls `preventDefault()` the banner removes ITSELF — unlike
 * `<caos-tag>`, which raises a cancelable event and then leaves the removing entirely to its host.
 * Two pieces in the same group, two different contracts; this is the one that acts.
 *
 * The stylesheet also answers to `intent="error"` as an alias for `danger`, and the alias reaches
 * the alert role and the glyph as well. `danger` is the spelling to write.
 */
const docs: ComponentDocs = {
  aliases: ['alert banner', 'callout', 'inline alert', 'message bar', 'admonition', 'info box'],

  answers: [
    'warn about something on this page that must not disappear',
    'ask for a decision before somebody carries on',
    'explain why a page is in a restricted or unusual mode',
    'announce an outage or a freeze on a screen',
    'say that the figures on this page are out of date',
  ],

  placements: ['recordPage', 'homePage', 'appPage', 'screen'],

  inputs: [
    {
      name: 'intent',
      attribute: 'intent',
      type: 'text',
      options: ['info', 'success', 'warning', 'danger', 'error'],
      description:
        'Which semantic tint to draw: info, success, warning or danger. These read the semantic ' +
        'colour tokens rather than the brand accent, so a danger banner stays red when a tenant ' +
        'repaints the accent. `error` is accepted as an alias for danger.',
      default: 'info',
    },
    {
      name: 'title',
      attribute: 'title',
      type: 'text',
      description:
        'The heading above the body, in the tint\'s darker on-tint ink. Unset, the heading line is not ' +
        'drawn and the body stands on its own. This is the standard HTML `title` attribute, so it is ' +
        'also the element\'s hover tooltip.',
    },
    {
      name: 'dismissable',
      attribute: 'dismissable',
      type: 'boolean',
      description:
        'Show the close control. Set it only where losing the notice costs nothing — a banner that ' +
        'carries a decision should not have one.',
      default: false,
    },
  ],

  events: [
    {
      name: 'caos-notice-dismiss',
      when:
        'The close control is pressed. It is cancelable, and it is the banner itself that acts on the ' +
        'result: unless a listener calls `preventDefault()`, the banner removes itself from the page. ' +
        'Cancel it to keep the banner and decide for yourself — to record the dismissal first, or to ' +
        'refuse it.',
      carries: 'nothing',
    },
  ],

  state: [],

  slots: [
    { name: '', takes: 'The body — the notice itself, in a sentence or two.' },
    { name: 'actions', takes: 'The decision controls, drawn at the right-hand end of the banner.' },
  ],

  tokens: [
    { token: '--caos-color-info-bg', controls: 'The fill of the default (info) banner; the edge is mixed from --caos-color-info.' },
    { token: '--caos-color-info', controls: 'The glyph on an info banner, and the colour its edge is mixed from.' },
    { token: '--caos-color-info-text', controls: 'The heading on an info banner — the on-tint ink, so it holds up in both themes.' },
    { token: '--caos-color-info-text-muted', controls: 'The body and the close control on an info banner.' },
    { token: '--caos-color-success-bg', controls: 'The fill of a success banner.' },
    { token: '--caos-color-success', controls: 'Its glyph, and the colour its edge is mixed from.' },
    { token: '--caos-color-success-text', controls: 'Its heading.' },
    { token: '--caos-color-success-text-muted', controls: 'Its body.' },
    { token: '--caos-color-warning-bg', controls: 'The fill of a warning banner.' },
    { token: '--caos-color-warning', controls: 'Its glyph, and the colour its edge is mixed from.' },
    { token: '--caos-color-warning-text', controls: 'Its heading.' },
    { token: '--caos-color-warning-text-muted', controls: 'Its body.' },
    { token: '--caos-color-danger-bg', controls: 'The fill of a danger banner.' },
    { token: '--caos-color-danger', controls: 'Its glyph, and the colour its edge is mixed from.' },
    { token: '--caos-color-danger-text', controls: 'Its heading.' },
    { token: '--caos-color-danger-text-muted', controls: 'Its body.' },
    { token: '--caos-radius', controls: 'The corner radius of the banner.' },
    { token: '--caos-radius-sm', controls: 'The corners of the close control and its focus ring.' },
    { token: '--caos-focus-ring', controls: 'The ring on the close control when it is reached by keyboard.' },
    { token: '--caos-font', controls: 'The typeface the banner is set in.' },
    { token: '--caos-text-row', controls: 'The size of the heading.' },
    { token: '--caos-space-4', controls: 'The horizontal padding.' },
    { token: '--caos-space-3', controls: 'The vertical padding, and the gap between the glyph and the content.' },
    { token: '--caos-space-2', controls: 'The gap between controls in the actions slot.' },
    { token: '--caos-space-1', controls: 'The inset of the first slotted action.' },
  ],

  parts: [
    { part: 'banner', is: 'the banner' },
    { part: 'icon', is: 'the intent glyph' },
    { part: 'content', is: 'the title and the body together' },
    { part: 'title', is: 'the title line' },
    { part: 'dismiss', is: 'the dismiss control' },
  ],

  methods: [],

  placing:
    'At the top of the region it qualifies and across the full width of it — above the page content ' +
    'for a page-wide condition, at the top of one section for a condition that belongs to that ' +
    'section only. It is a block element and takes whatever width it is given. Placed below the ' +
    'content it describes it is usually missed, and placed in a scrolling region it scrolls away, ' +
    'which defeats the point of choosing a banner over a toast.',

  whenToUse:
    'A condition the surface is in that must stay visible — because it needs a decision, because it ' +
    'changes how the page should be read, or because it will still be true in ten minutes.',

  insteadUse: [
    {
      name: 'Toast',
      when:
        'The message is the OUTCOME of something the person just did, and nothing is lost by missing ' +
        'it — "Changes saved", "Copied". The test is time and agency: a toast is gone in seconds and ' +
        'carries no decision, so anything a person has to act on, refer back to, or read again must ' +
        'not be one. Conversely, a banner for a routine save leaves a page accumulating strips of ' +
        'stale congratulation nobody clears.',
    },
    {
      name: 'ValidationSummary',
      when:
        'A save was rejected and there is a list of field errors. That piece counts them, links each ' +
        'one to its field and moves the caret there; a danger banner would say the same thing once, ' +
        'without any of the links.',
    },
    {
      name: 'ErrorPanel',
      when:
        'The surface itself failed and there is nothing to sit above. A banner qualifies content; when ' +
        'there is no content, the panel takes its place.',
    },
    {
      name: 'InlineHint',
      when:
        'The note explains one field and is true always, not just now. That belongs under the field, ' +
        'in muted text, not in a tinted strip across the page.',
    },
  ],

  accessibility:
    'The role is chosen from the intent and does not have to be set: info, success and warning are ' +
    '`role="status"`, announced politely at the next pause, and danger (or the `error` alias) is ' +
    '`role="alert"`, announced immediately. The glyph is hidden because the copy carries the meaning, ' +
    'and the close control is a real button with an accessible name and its own focus ring. Left to ' +
    'the author: reserving danger for what genuinely interrupts — every banner rendered as danger ' +
    'trains people to ignore alerts — and putting real focusable controls in the actions slot, since ' +
    'that is where a person announced at will go looking for the decision.',

  examples: [
    {
      key: 'closed-period',
      label: 'A condition of the page',
      summary:
        'The ordinary case: a heading and a sentence explaining something about this surface that was ' +
        'true before the person arrived and will still be true when they leave.',
      placement: {
        component: 'caos-notice-banner',
        inputs: { intent: 'info', title: 'This period is closed for editing' },
        children: ['The period this record belongs to closed on 30 June. An administrator has to reopen it before anything here can be changed.'],
      },
    },
    {
      key: 'decision',
      label: 'Asking for a decision',
      summary:
        'The reason a banner is not a toast: it stays on screen holding the two controls that resolve ' +
        'it, so the choice can be made after reading the page rather than in the four seconds before ' +
        'a notice fades.',
      placement: {
        component: 'caos-notice-banner',
        inputs: { intent: 'warning', title: 'This record has unsaved changes from another session' },
        children: [
          'Somebody else edited this record while you had it open. Choose which version to keep.',
          { component: 'caos-button', inputs: { slot: 'actions', variant: 'primary', size: 'sm' }, children: ['Keep mine'] },
          { component: 'caos-button', inputs: { slot: 'actions', size: 'sm' }, children: ['Load theirs'] },
        ],
      },
    },
    {
      key: 'intents',
      label: 'The four intents',
      summary:
        'Every tint side by side, so the right one can be picked by looking — and so the jump in ' +
        'loudness from warning to danger is visible before it is chosen for something routine.',
      placement: [
        { component: 'caos-notice-banner', inputs: { intent: 'info' }, children: ['This order is a revision of ORD-4471.'] },
        { component: 'caos-notice-banner', inputs: { intent: 'success' }, children: ['Approved by Dana Whitfield on 12 June.'] },
        { component: 'caos-notice-banner', inputs: { intent: 'warning' }, children: ['The due date is less than five working days away.'] },
        { component: 'caos-notice-banner', inputs: { intent: 'danger' }, children: ['This account is on hold. Nothing below can be submitted until it is cleared.'] },
      ],
    },
    {
      key: 'dismissable',
      label: 'Dismissable',
      summary:
        'The close control, and the part worth knowing: the banner takes ITSELF off the page unless a ' +
        'listener cancels the event. Use the reset control on this stage to bring it back.',
      placement: {
        component: 'caos-notice-banner',
        inputs: { intent: 'success', title: 'Reference data refreshed', dismissable: true },
        children: ['Regions, teams and price lists were updated from the source system this morning.'],
      },
    },
  ],
};

export default docs;
